/**
 * “Season so far” comparison: team kits in header, flag + league, stat grid with pill highlights.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { SoccerKit } from '../../ui/SoccerKit';
import { getSectionCardStyles } from '../SectionCard/SectionCard.styles';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { useTheme } from '../../../src/theme/useTheme';
import type {
  MatchHubH2HSeasonSnapshot,
  MatchHubTeamSide,
} from '../../../src/match/types/matchHub.types';
import { getMatchHubH2HSeasonSoFarCardStyles } from './MatchHubH2HSeasonSoFarCard.styles';

export interface MatchHubH2HSeasonSoFarCardProps {
  snapshot: MatchHubH2HSeasonSnapshot;
  home: MatchHubTeamSide;
  away: MatchHubTeamSide;
  leagueCountryCode: string;
}

function parseSeasonHeading(subtitle: string): { heading: string; leagueName: string } {
  const idx = subtitle.indexOf(' · ');
  if (idx === -1) {
    return { heading: 'Season so far', leagueName: subtitle.trim() };
  }
  return {
    heading: subtitle.slice(0, idx).trim(),
    leagueName: subtitle.slice(idx + 3).trim(),
  };
}

export const MatchHubH2HSeasonSoFarCard: React.FC<MatchHubH2HSeasonSoFarCardProps> = ({
  snapshot,
  home,
  away,
  leagueCountryCode,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubH2HSeasonSoFarCardStyles(theme), [theme]);
  const sectionCard = useMemo(() => getSectionCardStyles(theme), [theme]);
  const { heading, leagueName } = parseSeasonHeading(snapshot.subtitle);

  return (
    <View style={[sectionCard.card]} testID="match-hub-h2h-season-so-far">
      <View style={styles.headerRow}>
        <View style={styles.headerKitSlot}>
          <SoccerKit primary={home.kit.primary} secondary={home.kit.secondary} type={home.kit.type} size={36} />
        </View>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>{heading}</Text>
          <View style={styles.leagueRow}>
            <CountryFlag countryCode={leagueCountryCode} size="compact" accessibilityLabel="League country" />
            <Text style={styles.leagueName} numberOfLines={1}>
              {leagueName}
            </Text>
          </View>
        </View>
        <View style={styles.headerKitSlot}>
          <SoccerKit primary={away.kit.primary} secondary={away.kit.secondary} type={away.kit.type} size={36} />
        </View>
      </View>

      {snapshot.rows.map((row) => (
        <View key={row.label} style={styles.statRow}>
          <View style={[styles.statSide, styles.statSideLeft]}>
            {row.highlightSide === 'home' ? (
              <View style={styles.statPillInner}>
                <Text style={styles.statPillGlyph}>{row.homeDisplay}</Text>
              </View>
            ) : (
              <Text style={styles.statValueText}>{row.homeDisplay}</Text>
            )}
          </View>
          <Text style={styles.statCenterLabel} numberOfLines={2}>
            {row.label}
          </Text>
          <View style={[styles.statSide, styles.statSideRight]}>
            {row.highlightSide === 'away' ? (
              <View style={styles.statPillInner}>
                <Text style={styles.statPillGlyph}>{row.awayDisplay}</Text>
              </View>
            ) : (
              <Text style={styles.statValueText}>{row.awayDisplay}</Text>
            )}
          </View>
        </View>
      ))}
    </View>
  );
};
